import { StylesConfig } from "react-select"

import css from "styles/export.module.scss"
import iconCheck from "assets/img/icon-check.svg"
import imgSpinner from "assets/img/spinner.svg"
import { GroupedControl } from "config/constants"
import iconCheckboxChecked from "assets/img/icon-checkbox-checked.svg"
import iconCheckboxUnchecked from "assets/img/icon-checkbox-unchecked.svg"
import iconCheckboxCheckedDisabled from "assets/img/icon-checkbox-checked-disabled.svg"
import iconCheckboxUncheckedDisabled from "assets/img/icon-checkbox-unchecked-disabled.svg"

export type SelectTheme = "transparent"
export type SelectSize = "small"

export default ({
    isSelectSearch = false,
    isInvalid = false,
    isInvalidMenu = false,
    hasTooltip = false,
    grouped,
    menuWidth,
    menuAlign,
    hasSelectSearchValue,
    menuZIndex,
    hasMore,
    isMulti,
    theme,
    size,
    hideSelectedOptionIcon,
}: {
    isSelectSearch?: boolean
    hasSelectSearchValue?: boolean
    isInvalid?: boolean
    isInvalidMenu?: boolean
    hasTooltip?: boolean
    grouped?: GroupedControl
    menuWidth?: string
    menuAlign?: "left" | "right"
    menuZIndex?: number
    hasMore?: boolean
    isMulti?: boolean
    theme?: SelectTheme
    size?: SelectSize
    hideSelectedOptionIcon?: boolean
}): StylesConfig<any, boolean> => {
    return {
        control: (provided, state) => {
            let borderRadius = "4px"

            switch(grouped) {
                case GroupedControl.top:
                    borderRadius = "4px 4px 0 0"
                    break
                case GroupedControl.bottom:
                    borderRadius = "0 0 4px 4px"
                    break
                case GroupedControl.left:
                    borderRadius = "4px 0 0 4px"
                    break
                case GroupedControl.right:
                    borderRadius = "0 4px 4px 0"
                    break
                case GroupedControl.center:
                    borderRadius = "0"
                    break
            }

            let common = {
                ...provided,
                borderRadius,
                cursor: state.isDisabled ? "not-allowed" : "text",
                width: "100%",
                minHeight: size === "small" ? "24px" : "40px",
            }

            if (isSelectSearch) {
                let paddingRight = 0
                if (hasTooltip) {
                    paddingRight += 40
                }
                if (hasSelectSearchValue) {
                    paddingRight += 40
                }
                common.paddingRight = `${paddingRight}px`
            }

            if (state.isFocused) {
                return {
                    ...common,
                    backgroundColor: css.cWhite,
                    borderWidth: 1,
                    borderColor: isInvalid ? css.cRed500 : css.cGrey400,
                    boxShadow: "none",
                    "&:hover": {
                        borderColor: isInvalid ? css.cRed500 : css.cGrey400,
                    },
                }
            }

            const borerColorDefault = isInvalid ? css.cRed50 : css.cGrey100
            const borerColorHover = isInvalid ? css.cRed100 : css.cGrey200

            return {
                ...common,
                borderWidth: 1,
                borderColor: theme === "transparent" ? "transparent" : borerColorDefault,
                borderTopColor: !isInvalid && grouped && grouped === GroupedControl.bottom ? css.cGrey300 : (theme === "transparent" ? "transparent" : borerColorDefault),
                borderLeftColor: !isInvalid && grouped && (grouped === GroupedControl.right || grouped === GroupedControl.center) ? css.cGrey300 : (theme === "transparent" ? "transparent" : borerColorDefault),
                backgroundColor: theme === "transparent" ? "transparent" : (isInvalid ? css.cRed50 : css.cGrey100),
                color: isInvalid ? css.cRed500 : css.cBlack,
                boxShadow: "none",
                "&:hover": {
                    backgroundColor: isInvalid ? css.cRed100 : css.cGrey200,
                    borderColor: borerColorHover,
                    borderTopColor: !isInvalid && grouped && grouped === GroupedControl.bottom ? css.cGrey300 : borerColorHover,
                    borderLeftColor: !isInvalid && grouped && (grouped === GroupedControl.right || grouped === GroupedControl.center) ? css.cGrey300 : borerColorHover,
                }
            }
        },
        input: (provided, state) => {
            return {
                ...provided,
                color: "inherit",
                padding: "0",
                margin: "0",
                ...isMulti ? {} : {
                    display: "block",
                    width: "100%",
                    height: "100%",
                },
                ...isMulti ? {
                    padding: "0 10px",
                    marginRight: "-20px",
                } : {
                    // react-select 5's grid input autosizer adds a sizing ":after"
                    // pseudo-element that grows the control vertically; hide it to keep
                    // v4's compact height. Leave the grid placement intact so the input
                    // still overlays the value on one line (don't reset gridArea).
                    "&:after": { display: "none" },
                    ".select__input": {
                        padding: size === "small" ? "0 0 0 4px !important" : "0 10px !important",
                        width: "100% !important",
                        height: "100%",
                        boxSizing: "border-box !important" as "border-box",
                    },
                },
            }
        },
        container: (process, state) => {
            return {
                ...process,
                ...state.isDisabled ? {
                    opacity: 0.6,
                } : {}
            }
        },
        indicatorsContainer: (provided) => {
            return {
                ...provided,
                overflow: "hidden",
            }
        },
        clearIndicator: (provided, state) => {
            return {
                ...provided,
                padding: "14px",
                cursor: "pointer"
            }
        },
        dropdownIndicator: (provided, state) => {
            return {
                ...provided,
                color: state.isDisabled ? css.cGrey400 : css.cBlack,
                padding: size === "small" ? "4px" : "7px",
                opacity: state.isDisabled ? 0.5 : 1,
                transition: "all .1s ease-in-out",
                transform: state.selectProps.menuIsOpen ? "rotate(-180deg)" : "none",
                width: size === "small" ? "24px" : "38px",
                height: size === "small" ? "24px" : "38px",
                "svg": {
                    width: "100%",
                    height: "100%",
                }
            }
        },
        option: (provided, state) => {
            return {
                ...provided,
                backgroundColor: state.isFocused ? css.cGrey100 : "transparent",
                wordBreak: "break-word",
                position: "relative",
                paddingRight: !state.isMulti && state.isSelected && !hideSelectedOptionIcon ? "50px" : "10px",
                paddingLeft: state.isMulti ? "46px" : "10px",
                color: state.isDisabled ? css.cGrey400 : css.cBlack,
                ...(!state.isMulti && state.isSelected && !hideSelectedOptionIcon ? {
                    "&:after": {
                        content: "''",
                        width: "24px",
                        height: "24px",
                        backgroundImage: `url(${iconCheck})`,
                        backgroundRepeat: "no-repeat",
                        position: "absolute",
                        top: "50%",
                        right: "20px",
                        transform: "translateY(-50%)"
                    }
                } : {}),
                ...(state.isMulti ? {
                    "&:after": {
                        content: "''",
                        width: "18px",
                        height: "18px",
                        backgroundImage: `url(${state.isDisabled
                            ? (state.isSelected && !hideSelectedOptionIcon ? iconCheckboxCheckedDisabled : iconCheckboxUncheckedDisabled)
                            : (state.isSelected && !hideSelectedOptionIcon ? iconCheckboxChecked : iconCheckboxUnchecked)
                        })`,
                        backgroundRepeat: "no-repeat",
                        position: "absolute",
                        top: "50%",
                        left: "16px",
                        transform: "translateY(-50%)"
                    }
                } : {}),
                "&:hover": {
                    backgroundColor: state.isDisabled ? css.cWhite : css.cGrey100
                },
                "&:active": {
                    backgroundColor: state.isDisabled ? css.cWhite : css.cGrey100
                }
            }
        },
        menu: (provided, state) => {
            const additional: any = {
                boxShadow: "0px 6px 18px rgba(0, 0, 0, 0.197006);",
                zIndex: typeof menuZIndex === "number" ? menuZIndex : 700,
            }

            if (menuWidth) {
                additional.width = menuWidth
            }

            if (menuAlign === "left") {
                additional.left = "0"
            }
            if (menuAlign === "right") {
                additional.right = "0"
            }

            if (isInvalidMenu) {
                additional.color = css.cRed500
            }

            return {
                ...provided,
                ...additional,
            }
        },
        menuList: (provided, state) => {
            const additional: any = hasMore && !!state.options && state.options.length > 0 ? {
                "&:after": {
                    content: "''",
                    width: "3rem",
                    height: "3rem",
                    margin: "1rem auto",
                    display: "block",
                    background: `url(${imgSpinner}) no-repeat center / contain`,
                    animation: "spinner .65s infinite linear",
                }
            } : {}

            return {
                ...provided,
                ...additional,
            }
        },
        group: (provided, state) => {
            return {
                ...provided,
                "&:not(:last-child)": {
                    paddingBottom: 0
                }
            }
        },
        groupHeading: (provided, state) => {
            return {
                ...provided,
                borderBottom: `1px solid ${css.cGrey100}`,
                color: css.cGrey600,
                fontWeight: "bold",
                fontSize: "12px",
                lineHeight: "20px",
                textTransform: "none",
                paddingTop: "1rem",
                paddingBottom: "1rem",
            }
        },
        placeholder: (provided, state) => ({
            ...provided,
            color: isInvalid && !state.isFocused ? css.cRed300 : css.cGrey500,
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
            width: state.isMulti ? "auto" : "100%",
            paddingLeft: "10px",
            paddingRight: "10px",
            margin: 0,
            transform: "none",
            top: "auto",
            position: state.isMulti ? "static" : "absolute",
        }),
        singleValue: (provided, state) => ({
            ...provided,
            marginLeft: size === "small" ? "4px" : "10px",
            marginRight: size === "small" ? 0 : "10px",
            color: isInvalid ? css.cRed500 : css.cBlack,
        }),
        valueContainer: (provided, state) => ({
            ...provided,
            minHeight: size === "small" ? "22px" : "38px",
            padding: state.isMulti ? "4px 0" : "0",
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: typeof menuZIndex === "number" ? menuZIndex : provided.zIndex,
        }),
        multiValue: (provided, state) => {
            return {
                ...provided,
                backgroundColor: state.data.isFixed ? css.cGrey200 : css.cBlack,
                borderRadius: "100px",
                padding: 0,
                marginLeft: "8px",
                marginRight: 0,
                cursor: "default",
            }
        },
        multiValueLabel: (base, state) => {
            return {
                ...base,
                fontWeight: "bold",
                color: state.data.isFixed ? css.cGrey400 : css.cWhite,
                fontSize: css.pSmallestFontSize,
                lineHeight: css.pSmallestLineHeight,
                padding: "2px 8px !important",
            }
        },
        multiValueRemove: (provided, state) => {
            return {
                ...provided,
                display: state.data.isFixed ? "none" : "flex",
                paddingLeft: "8px",
                paddingRight: "8px",
                marginLeft: "-8px",
                cursor: "pointer",
                borderRadius: "50%",
                "&:hover": {
                    backgroundColor: "transparent !important",
                },
            }
        },
    }
}
